import React, { useEffect, useState, useCallback } from "react";
import { Layout, Menu, Button, Avatar, Dropdown, Row, Col, Image, Grid, Modal } from "antd";
import {
    MenuUnfoldOutlined,
    MenuFoldOutlined,
    ExclamationCircleOutlined,
} from "@ant-design/icons";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import LanguageSwitcher from "../LanguageSwitcher/LanguageSwitcher";
import "./MainLayout.scss";
import blueitLogo from "../../assets/icons/blueit_logo.svg";
import blueitLogoWithName from "../../assets/icons/blueit_logo_with_name.svg";
import type { MenuProps } from 'antd';
import { useDispatch } from "react-redux";
import menAvatar from "../../assets/icons/men-avatar.png"
import dashboardIcon from "../../../src/assets/icons/dashboardLayoutIcon.svg"
import logoutIcon from "../../../src/assets/icons/logoutLayoutIcon.svg"
import integrationIcon from "../../../src/assets/icons/integrations.svg";
import MenuIcon from "../MenuIcons/MenuIcons";
import clientsIcon from "../../../src/assets/icons/clients.svg"


const { Header, Sider, Content } = Layout;
const { useBreakpoint } = Grid;
const { confirm } = Modal;

interface MainLayoutProps {
    children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [collapsed, setCollapsed] = useState(false);
    const { t } = useTranslation('layout');
    const { logout } = useAuth();
    const location = useLocation();

    const screens = useBreakpoint();
    const getDefaultOpenKeys = () => {
        if (location.pathname.includes('/integrations')) {
            return ['integrations'];
        }
        return [];
    };

    const [openKeys, setOpenKeys] = useState<string[]>(getDefaultOpenKeys());

    const onOpenChange: MenuProps['onOpenChange'] = (keys) => {
        setOpenKeys(keys);
    };

    const showLogoutConfirm = useCallback(() => {
        confirm({
            title: t("logout_confirm_title"),
            content: t("logout_confirm_content"),
            icon: <ExclamationCircleOutlined />,
            okText: t("logout"),
            cancelText: t("cancel"),
            okType: 'danger',
            onOk() {
                logout();
            },
        });
    }, [logout]);

    const handleMenuClick = useCallback(({ key }: { key: string }) => {
        if (key === "/logout") {
            showLogoutConfirm();
        }
    }, [showLogoutConfirm]);

    const menuItems: MenuProps['items'] = [
        {
            key: "/dashboard",
            icon: <MenuIcon
                src={dashboardIcon}
                alt="Dashboard"
                isActive={location.pathname === "/dashboard"}
            />,
            label: <Link to="/dashboard">{t("dashboard")}</Link>,
        },
        {
            key: "/clients",
            icon: <MenuIcon
                src={clientsIcon}
                alt="Clients"
                isActive={location.pathname === "/clients"}
            />,
            label: <Link to="/clients">{t("clients")}</Link>,
        },
        {
            key: "integrations",
            icon: <MenuIcon
                src={integrationIcon}
                alt="Integrations"
                isActive={location.pathname.includes('/integrations')}
                isSubMenuOpen={openKeys.includes('integrations')}
            />,
            label: t("integrations"),
            children: [
                {
                    key: "/integrations/dijikolay",
                    label: <Link to="/integrations/dijikolay">{t("dijikolay")}</Link>,
                },
            ],
        },
        {
            key: "divider-1",
            type: 'divider',
        },
        {
            key: "/logout",
            icon: <MenuIcon
                src={logoutIcon}
                alt="Logout"
                isActive={false}
            />,
            label: t("logout"),
        },
    ];


    useEffect(() => {
        setOpenKeys(getDefaultOpenKeys());
    }, [location.pathname]);

    useEffect(() => {
        if (screens.xl || screens.xxl) {
            setCollapsed(false);
        } else {
            setCollapsed(true);
        }
    }, [screens]);

    return (
        <Layout className="main-layout" style={{ background: "white" }}>
            <Sider
                className="sider"
                trigger={null}
                collapsible
                collapsed={collapsed}
                width={272}
                style={{ height: "100vh", position: "fixed", left: 0, overflow: "hidden" }}
            >
                <Row className="logo" justify="space-between" align="middle">
                    <Image
                        preview={false}
                        src={collapsed ? blueitLogo : blueitLogoWithName}
                        alt="Logo"
                        style={{
                            height: '31px',
                            width: collapsed ? '32px' : 'auto',
                            objectFit: 'contain'
                        }}
                    />
                    <Button
                        type="text"
                        icon={
                            collapsed ? (
                                <MenuUnfoldOutlined style={{ color: "#02B9D9" }} />
                            ) : (
                                <MenuFoldOutlined style={{ color: "#02B9D9" }} />
                            )
                        }
                        onClick={() => setCollapsed(!collapsed)}
                    />
                </Row>

                <Menu
                    theme="light"
                    mode="inline"
                    selectedKeys={[location.pathname]}
                    openKeys={openKeys}
                    onOpenChange={onOpenChange}
                    items={menuItems}
                    className="custom-menu"
                    onClick={handleMenuClick}
                />
            </Sider>
            <Layout style={{ marginLeft: collapsed ? 80 : 272, height: "100vh", overflow: "auto", background: "white" }}>
                <Header className="header" style={{ padding: 0, background: "#fff" }}>
                    <Row className="header-container">
                        <Col className="header-right">
                            <LanguageSwitcher />
                            <Row className="user-dropdown-trigger">
                                <Avatar size={50} src={menAvatar} />
                                <Col className="username-container">
                                    <Row className="username">Emre</Row>
                                    <Row className="title">Manager</Row>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Header>
                <Content
                    style={{
                        padding: 24,
                        minHeight: "100vh",
                        backgroundColor: 'white',
                    }}
                >
                    {children}
                </Content>
            </Layout>
        </Layout>
    );
};

export default MainLayout;